/**
 * Automation Mode Service
 *
 * API service for managing automation mode (Pilot/Copilot/Autopilot) settings.
 * Part of Epic APB-1054: [SOAR] Epic 1: Co-Pilot Activation
 * Ticket: APB-1166 - Mode Configuration UI
 *
 * Integrates with APB-1055 (AutomationModeService) backend endpoints:
 * - GET  /api/automation/mode     - Get current mode
 * - PUT  /api/automation/mode     - Set mode
 * - GET  /api/automation/settings - Get full settings
 * - PATCH /api/automation/settings - Update settings
 * - POST /api/automation/can-execute - Check if action can auto-execute
 * - GET  /api/automation/modes    - List all available modes
 */

// Types
export type AutomationMode = 'pilot' | 'copilot' | 'autopilot';

export interface AutomationModeResponse {
  mode: AutomationMode;
  display_name: string;
  description: string;
  soar_enabled: boolean;
}

export interface AutomationModeInfo {
  mode: AutomationMode;
  name: string;
  description: string;
  icon: string;
  color: string;
  risk_level: 'low' | 'medium' | 'high';
}

export interface ApprovalSettings {
  requires_approval: boolean;
  requires_2fa: boolean;
  timeout_minutes: number;
  auto_escalate: boolean;
}

export interface AutopilotSettings {
  confidence_threshold: number;
  max_severity: string;
  excluded_actions: string[];
  require_2fa_critical: boolean;
}

export interface AutomationStatistics {
  total_actions_executed: number;
  total_actions_approved: number;
  total_actions_rejected: number;
  last_action_at: string | null;
}

export type ExecutionMode = 'recommend_only' | 'brain_executes' | 'your_soar_executes' | 'best_available';

export interface ExecutionModeResponse {
  execution_mode: ExecutionMode;
  display_name: string;
  description: string;
}

export interface ExecutionModeInfo {
  execution_mode: ExecutionMode;
  name: string;
  description: string;
  icon: string;
  color: string;
  risk_level: string;
}

export interface AutomationSettingsResponse {
  mode: AutomationMode;
  soar_enabled: boolean;
  config: {
    action_behavior: string;
    approval_required: boolean;
  };
  approval_settings: ApprovalSettings;
  autopilot_settings: AutopilotSettings;
  execution_mode: ExecutionMode;
  statistics: AutomationStatistics;
}

export interface CopilotSettingsUpdate {
  require_2fa?: boolean;
  approval_timeout_minutes?: number;
  auto_escalate_on_timeout?: boolean;
}

export interface AutopilotSettingsUpdate {
  confidence_threshold?: number;
  max_severity?: string;
  excluded_action_types?: string[];
  require_2fa_critical?: boolean;
}

export interface AutomationSettingsUpdate {
  automation_mode?: AutomationMode;
  execution_mode?: ExecutionMode;
  soar_enabled?: boolean;
  playbook_auto_trigger?: boolean;
  copilot_settings?: CopilotSettingsUpdate;
  autopilot_settings?: AutopilotSettingsUpdate;
  mode_specific_config?: Record<string, unknown>;
}

export interface CanExecuteRequest {
  confidence_score: number;
  severity: string;
  action_type: string;
}

export interface CanExecuteResponse {
  can_execute: boolean;
  requires_approval: boolean;
  requires_2fa: boolean;
  reason: string | null;
}

// API Error type
export interface ApiError {
  detail: string;
  status?: number;
}

// Helper function to get auth headers
const getAuthHeaders = (): Record<string, string> => {
  const authToken = localStorage.getItem('astrapeak_auth_token');
  const tenantId = localStorage.getItem('tenant_id') || '';

  return {
    'Content-Type': 'application/json',
    'X-Tenant-ID': tenantId,
    'Authorization': `Bearer ${authToken}`,
  };
};

// Helper function to handle API errors.
// HTTP/2 strips response.statusText, so we always include the numeric status
// code — otherwise consumers see "API Error: " with no signal about whether
// they hit a 401 (auth), 403 (perm), 404 (missing), or 500 (server).
const handleApiError = async (response: Response): Promise<never> => {
  const statusLabel = response.statusText || `HTTP ${response.status}`;
  let errorMessage = `API Error ${response.status}: ${statusLabel}`;

  try {
    const errorData = await response.json();
    if (errorData?.detail) {
      errorMessage = `API Error ${response.status}: ${errorData.detail}`;
    }
  } catch {
    // Response body was not JSON — keep the status-code message above.
  }

  const error = new Error(errorMessage) as Error & { status: number };
  error.status = response.status;
  throw error;
};

/**
 * Automation Mode Service
 *
 * Provides methods for interacting with the automation mode API endpoints.
 */
export const automationModeService = {
  /**
   * Get the current automation mode for the tenant
   */
  async getMode(): Promise<AutomationModeResponse> {
    const response = await fetch('/api/automation/mode', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Set the automation mode for the tenant
   */
  async setMode(mode: AutomationMode): Promise<AutomationModeResponse> {
    const response = await fetch('/api/automation/mode', {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ mode }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Get complete automation settings
   */
  async getSettings(): Promise<AutomationSettingsResponse> {
    const response = await fetch('/api/automation/settings', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Update automation settings
   */
  async updateSettings(updates: AutomationSettingsUpdate): Promise<AutomationSettingsResponse> {
    const response = await fetch('/api/automation/settings', {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Check if an action can be auto-executed
   */
  async checkCanExecute(request: CanExecuteRequest): Promise<CanExecuteResponse> {
    const response = await fetch('/api/automation/can-execute', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Get all available automation modes with descriptions
   */
  async listModes(): Promise<AutomationModeInfo[]> {
    const response = await fetch('/api/automation/modes', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Get the current execution mode for the tenant
   */
  async getExecutionMode(): Promise<ExecutionModeResponse> {
    const response = await fetch('/api/automation/execution-mode', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Set the execution mode for the tenant
   */
  async setExecutionMode(execution_mode: ExecutionMode): Promise<ExecutionModeResponse> {
    const response = await fetch('/api/automation/execution-mode', {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ execution_mode }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },

  /**
   * Get all available execution modes with descriptions
   */
  async listExecutionModes(): Promise<ExecutionModeInfo[]> {
    const response = await fetch('/api/automation/execution-modes', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return response.json();
  },
};

export default automationModeService;
